import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Megaphone,
  User,
  FileText,
  Bell,
  BellRing,
  Smartphone,
  Send,
  ChevronDown,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { getNotificationLogs, sendDriverPushNotification } from "api/notifications";
import { showToast } from "utils/showToast";
import { getActiveDeliveryUsers } from "api/user";
import { Card } from "components/ui/Card";
import { Button } from "components/ui/Button";
import { Input } from "components/ui/Input";
import { Badge } from "components/ui/Badge";
import { SearchableSelect } from "components/ui/SearchableSelect";
import { RelativeTime } from "components/ui/RelativeTime";
import useAuthScope from "hooks/useAuthScope";

const notificationTemplatesConfig = [
  {
    id: "flex-close",
    titleKey: "Cierra pedidos en Flex",
    bodyKey: "Recuerda cerrar tus pedidos de MercadoLibre en la app de Flex. Sin el cierre correcto, los pedidos quedan pendientes y afectan tu desempeño.",
  },
  {
    id: "complete-withdrawals",
    titleKey: "Completa tus retiros",
    bodyKey: "Recuerda terminar tus retiros en las instalaciones del comercio. Asegúrate de retirar todos los paquetes asignados antes de salir.",
  },
  {
    id: "upload-evidence",
    titleKey: "Sube evidencia de entrega",
    bodyKey: "Recuerda subir 3 fotos de evidencia por cada entrega. Las fotos son obligatorias para acreditar la entrega y evitar reclamos.",
  },
  {
    id: "early-route",
    titleKey: "Sal temprano a ruta",
    bodyKey: "Recuerda salir temprano a ruta para completar tus entregas antes de las 21:00. Planifica bien tu recorrido y prioriza las más lejanas.",
  },
];

const segBtn = (active) =>
  [
    "flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-2 sm:py-1.5 min-h-[44px] sm:min-h-0",
    "text-sm font-medium whitespace-nowrap transition-colors",
    active
      ? "bg-bg text-brand-600 shadow-card"
      : "text-fg-muted hover:text-fg",
  ].join(" ");

export default function DriverNotificationsPanel() {
  const { t } = useTranslation();
  const notificationTemplates = notificationTemplatesConfig.map((tpl) => ({
    ...tpl,
    title: t(tpl.titleKey),
    body: t(tpl.bodyKey),
  }));
  const priorityLabels = { high: t("Alta"), standard: t("Normal") };
  const { logisticCompanyId } = useAuthScope();
  const [audience, setAudience] = useState("all");
  const [driverId, setDriverId] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [deeplink, setDeeplink] = useState("");
  const [priority, setPriority] = useState("standard");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoadingLogs(true);
    const { success, data } = await getNotificationLogs(1, 10);
    if (success) setLogs(data?.docs ?? []);
    setLoadingLogs(false);
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    if (!logisticCompanyId) return undefined;
    let mounted = true;
    setLoadingDrivers(true);
    getActiveDeliveryUsers({ logisticCompanyId, limit: 1000 }).then(
      ({ success, data, error }) => {
        if (!mounted) return;
        if (!success) {
          showToast(error?.message || t("No se pudieron cargar los repartidores"), false);
          setLoadingDrivers(false);
          return;
        }
        const docs = data?.docs || [];
        setDrivers(
          docs.map((d) => ({
            value: d._id || d.id,
            label: `${d.name || t("Sin nombre")} ${d.lastName || ""} · ${d.email || t("sin email")}`,
          }))
        );
        setLoadingDrivers(false);
      }
    );
    return () => {
      mounted = false;
    };
  }, [logisticCompanyId]);

  const previewTitle = title || t("Título pendiente");
  const previewBody = body || t("El contenido de la notificación aparecerá aquí. Mantén el mensaje breve y accionable.");
  const previewTarget =
    audience === "all"
      ? t("Todos los repartidores")
      : drivers.find((d) => d.value === driverId)?.label || driverId || t("Repartidor seleccionado");

  const canSend = useMemo(
    () =>
      title.trim().length > 0 &&
      body.trim().length > 0 &&
      (audience === "all" || driverId.trim().length > 0),
    [audience, body, driverId, title]
  );

  const handleTemplateClick = (template) => {
    setTitle(template.title);
    setBody(template.body);
    setStatus(null);
  };

  const resetForm = () => {
    setTitle("");
    setBody("");
    setDeeplink("");
    setPriority("standard");
    setDriverId("");
    setStatus(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSend) {
      setStatus({ type: "error", message: t("Completa título, mensaje y destinatario.") });
      return;
    }
    setLoading(true);
    setStatus(null);

    const payload = {
      title: title.trim(),
      message: body.trim(),
      deeplink: deeplink.trim() || undefined,
      priority,
      audience: audience === "all" ? "all" : "users",
      userIds: audience === "single" ? [driverId.trim()] : undefined,
    };

    const response = await sendDriverPushNotification(payload);
    setLoading(false);

    if (!response.success) {
      setStatus({ type: "error", message: response.error?.message || t("No se pudo enviar.") });
      return;
    }

    const count = response.data?.tokensEncolados ?? 0;
    setStatus({ type: "success", message: t("Notificación encolada ({{count}} tokens).", { count }) });
    showToast(t("Notificación encolada a repartidores"), true);
    fetchLogs();
  };

  const fieldLabel = "text-sm font-semibold text-fg";
  const fieldHint = "text-xs text-fg-subtle";

  return (
    <div className="w-full min-w-0 flex flex-col gap-5">
      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        {/* ── Audience ── */}
        <Card className="flex flex-col gap-4">
          <div className="grid grid-cols-1 sm:inline-flex w-full sm:w-fit gap-1 rounded-2xl sm:rounded-full bg-bg-muted p-1">
            <button
              type="button"
              aria-pressed={audience === "all"}
              className={segBtn(audience === "all")}
              onClick={() => setAudience("all")}
            >
              <Megaphone className="w-4 h-4" />
              {t("Todos los repartidores")}
            </button>
            <button
              type="button"
              aria-pressed={audience === "single"}
              className={segBtn(audience === "single")}
              onClick={() => setAudience("single")}
            >
              <User className="w-4 h-4" />
              {t("Repartidor específico")}
            </button>
          </div>

          {audience === "single" && (
            <div className="max-w-md">
              <SearchableSelect
                options={drivers}
                placeholder={loadingDrivers ? t("Cargando repartidores...") : t("Buscar repartidor")}
                value={driverId}
                onChange={(value) => setDriverId(value)}
                isDisabled={loadingDrivers}
                isClearable
                noOptionsText={t("Sin resultados")}
              />
            </div>
          )}
        </Card>

        {/* ── Compose + Preview grid ── */}
        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-5 items-start">
          {/* Compose card */}
          <Card className="flex flex-col gap-5">
            <h3 className="text-base font-semibold text-fg">{t("Mensajes predeterminados")}</h3>

            {/* Templates */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
              {notificationTemplates.map((template) => (
                <button
                  key={template.id}
                  type="button"
                  className="flex items-start gap-2.5 rounded-xl border border-border bg-bg-muted p-3 text-left transition-colors hover:border-brand-500 hover:bg-brand-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                  onClick={() => handleTemplateClick(template)}
                >
                  <FileText className="w-4 h-4 mt-0.5 shrink-0 text-brand-600" />
                  <span className="min-w-0">
                    <strong className="block text-sm font-semibold text-fg">{template.title}</strong>
                    <span className="mt-1 block text-xs text-fg-muted line-clamp-2">{template.body}</span>
                  </span>
                </button>
              ))}
            </div>

            {/* Fields */}
            <label className="flex flex-col gap-1.5">
              <span className={fieldLabel}>{t("Título")}</span>
              <Input
                type="text"
                placeholder={t("Ej: Pausa preventiva en tu ruta")}
                value={title}
                maxLength={70}
                onChange={(e) => setTitle(e.target.value)}
              />
              <small className={fieldHint}>{title.length}/70 {t("caracteres")}</small>
            </label>

            <label className="flex flex-col gap-1.5">
              <span className={fieldLabel}>{t("Mensaje")}</span>
              <textarea
                rows={4}
                placeholder={t("Describe el contexto y la acción esperada.")}
                value={body}
                maxLength={280}
                onChange={(e) => setBody(e.target.value)}
                className="w-full resize-y rounded-lg border border-border bg-bg px-3 py-2 text-sm text-fg placeholder:text-fg-subtle focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              />
              <small className={fieldHint}>{body.length}/280 {t("caracteres")}</small>
            </label>

            <label className="flex flex-col gap-1.5">
              <span className={fieldLabel}>
                Deep link <em className="font-normal not-italic text-fg-muted">({t("opcional")})</em>
              </span>
              <Input
                type="text"
                placeholder="beartrack://routes/today"
                value={deeplink}
                onChange={(e) => setDeeplink(e.target.value)}
              />
              <small className={fieldHint}>{t("Abre una pantalla específica al tocar la notificación.")}</small>
            </label>

            {/* Priority */}
            <div className="flex flex-wrap items-center gap-3">
              <span className={fieldLabel}>{t("Prioridad")}</span>
              <div className="inline-flex gap-1 rounded-full bg-bg-muted p-1">
                <button
                  type="button"
                  aria-pressed={priority === "standard"}
                  className={segBtn(priority === "standard")}
                  onClick={() => setPriority("standard")}
                >
                  <Bell className="w-4 h-4" />
                  {t("Normal")}
                </button>
                <button
                  type="button"
                  aria-pressed={priority === "high"}
                  className={[
                    "flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-2 sm:py-1.5 min-h-[44px] sm:min-h-0 text-sm font-medium whitespace-nowrap transition-colors",
                    priority === "high"
                      ? "bg-bg text-status-error shadow-card"
                      : "text-fg-muted hover:text-fg",
                  ].join(" ")}
                  onClick={() => setPriority("high")}
                >
                  <BellRing className="w-4 h-4" />
                  {t("Alta")}
                </button>
              </div>
            </div>

            {/* Status */}
            {status && (
              <div
                role="status"
                className={[
                  "flex items-start gap-2 rounded-lg px-3 py-2.5 text-sm",
                  status.type === "success"
                    ? "bg-status-success-soft text-status-success"
                    : "bg-status-error-soft text-status-error",
                ].join(" ")}
              >
                {status.type === "success" ? (
                  <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                ) : (
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                )}
                <span>{status.message}</span>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-wrap justify-end gap-3">
              <Button variant="outline" onClick={resetForm} disabled={loading}>
                {t("Limpiar campos")}
              </Button>
              <Button type="submit" disabled={!canSend || loading} loading={loading} iconLeft={<Send className="w-4 h-4" />}>
                {loading ? t("Enviando...") : t("Enviar notificación")}
              </Button>
            </div>
          </Card>

          {/* Preview card */}
          <Card className="xl:sticky xl:top-4 flex flex-col gap-3.5">
            <div className="flex items-center gap-2.5 border-b border-border pb-3.5">
              <Smartphone className="w-6 h-6 text-fg-muted" />
              <div>
                <h3 className="text-sm font-semibold text-fg">{t("Vista previa")}</h3>
                <p className="text-xs text-fg-muted">Beartrack Drivers</p>
              </div>
            </div>
            <div
              className={[
                "flex flex-col gap-1.5 rounded-[4px_14px_14px_14px] p-3.5",
                priority === "high" ? "bg-status-error-soft" : "bg-brand-50",
              ].join(" ")}
            >
              <p className="text-[0.7rem] font-semibold uppercase tracking-wide text-fg-subtle">
                {previewTarget}
              </p>
              <h4 className="my-0.5 text-sm font-semibold text-fg">{previewTitle}</h4>
              <p className="text-sm leading-relaxed text-fg-muted">{previewBody}</p>
              <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2">
                <Badge tone={priority === "high" ? "error" : "brand"}>
                  {priorityLabels[priority]}
                </Badge>
                <span className="text-xs text-fg-subtle truncate">{deeplink || t("Sin deep link")}</span>
              </div>
            </div>
          </Card>
        </div>
      </form>

      {/* ── History ── */}
      <Card padding="none" className="overflow-hidden">
        <button
          type="button"
          className="flex w-full items-center justify-between gap-2 px-5 py-3.5 text-left text-sm font-semibold text-fg transition-colors hover:bg-bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          onClick={() => setHistoryOpen((v) => !v)}
          aria-expanded={historyOpen}
        >
          <span className="flex items-center gap-2">
            {t("Historial de envíos")}
            {logs.length > 0 && (
              <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-brand-500 px-1.5 text-[0.7rem] font-bold text-white">
                {logs.length}
              </span>
            )}
          </span>
          <ChevronDown
            className={`w-5 h-5 text-fg-muted transition-transform ${historyOpen ? "rotate-180" : ""}`}
          />
        </button>

        {historyOpen && (
          <div className="px-5 pb-4">
            {loadingLogs ? (
              <p className="m-0 text-sm text-fg-muted">{t("Cargando historial…")}</p>
            ) : logs.length === 0 ? (
              <p className="m-0 text-sm text-fg-muted">
                {t("Aún no se han enviado notificaciones.")}
              </p>
            ) : (
              <ul className="m-0 flex list-none flex-col p-0">
                {logs.map((item) => {
                  const audienceLabel =
                    item.audience === "all"
                      ? t("Todos los repartidores")
                      : `${item.totalTargeted} ${t("repartidor")}${item.totalTargeted !== 1 ? "es" : ""}`;

                  return (
                    <li
                      key={item._id}
                      className="flex flex-col gap-3 border-b border-border py-3 last:border-b-0 sm:flex-row sm:justify-between"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="m-0 flex flex-wrap items-center gap-2 text-sm font-semibold text-fg">
                          {item.title}
                          <Badge tone={item.priority === "high" ? "error" : "brand"}>
                            {priorityLabels[item.priority] || t("Normal")}
                          </Badge>
                        </p>
                        <p className="mt-1 text-sm text-fg-muted">{audienceLabel}</p>
                        <span className="mt-0.5 block text-xs text-fg-subtle">
                          {item.createdAt ? <RelativeTime value={item.createdAt} t={t} /> : '-'}
                          {item.sentByName ? ` · ${item.sentByName}` : ""}
                        </span>
                      </div>
                      <div className="flex shrink-0 flex-col gap-0.5 sm:min-w-[175px]">
                        <span className="flex items-center gap-1.5 text-sm font-semibold text-fg">
                          <Send className="w-4 h-4 text-brand-600" />
                          {item.totalTargeted} tokens
                        </span>
                        <small className="text-xs text-fg-subtle">{item.status}</small>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
